import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { RiCheckLine, RiFileCopyLine } from '@remixicon/react';

import { Button, ButtonIcon } from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';

import { useAlert } from '../../hooks/useAlert';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

export type CopyProps = {
  value: string | (() => string);
  label?: string;
  labelCopied?: string;
  variant?: 'default' | 'compact';
  size?: 'small' | 'medium';
  className?: string;
};

const useCopyIndicator = ({
  copied,
  size,
  labelDefault,
  labelCopied,
}: {
  copied: boolean;
  size: NonNullable<CopyProps['size']>;
  labelDefault: NonNullable<CopyProps['label']>;
  labelCopied: NonNullable<CopyProps['labelCopied']>;
}): { icon: ReactNode; label: string } =>
  useMemo(() => {
    const iconSize = size === 'small' ? 16 : 20;

    if (copied) {
      return {
        icon: (
          <RiCheckLine
            size={iconSize}
            aria-hidden
            data-testid="copy-icon-check"
          />
        ),
        label: labelCopied,
      };
    }

    return {
      icon: (
        <RiFileCopyLine
          size={iconSize}
          aria-hidden
          data-testid="copy-icon-default"
        />
      ),
      label: labelDefault,
    };
  }, [copied, size, labelDefault, labelCopied]);

export const Copy = ({
  value,
  label: labelDefault = 'Copy to clipboard',
  labelCopied = 'Copied',
  variant = 'default',
  size = 'small',
  className,
}: CopyProps) => {
  const alert = useAlert();
  const { copied, copy } = useCopyToClipboard();

  const handleCopy = async () => {
    const resolved = typeof value === 'function' ? value() : value;
    if (!resolved) return;

    const didCopy = await copy(resolved);
    alert.post(
      didCopy
        ? {
            message: 'Copied to clipboard',
            severity: 'success',
            display: 'transient',
          }
        : {
            message: 'Could not copy to clipboard',
            severity: 'error',
            display: 'transient',
          },
    );
  };

  const { icon, label } = useCopyIndicator({
    copied,
    size,
    labelDefault,
    labelCopied,
  });

  if (variant === 'default') {
    return (
      <Button
        variant="secondary-bordered"
        size={size}
        className={className}
        onClick={handleCopy}
      >
        {icon} {label}
      </Button>
    );
  }

  return (
    <TooltipRoot>
      <ButtonIcon
        variant="tertiary"
        size={size}
        className={className}
        aria-label={label}
        onClick={handleCopy}
      >
        {icon}
      </ButtonIcon>
      <Tooltip>{label}</Tooltip>
    </TooltipRoot>
  );
};
